import {
  Box,
  Button,
  ButtonLink,
  Card,
  CardBody,
  Container,
  Flex,
  Grid,
  Header,
  Select,
  Tag,
  TagGroup,
  Text,
  Tooltip,
  TooltipTrigger,
} from '@backstage/ui';
import {
  RiArrowRightLine,
  RiInformationLine,
  RiTeamFill,
} from '@remixicon/react';
import { useRouteRef } from '@backstage/frontend-plugin-api';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { Key } from 'react-aria-components';
import { useInsightsTracks, useTeamOwnerFilter } from '../../hooks';
import { buildEntityOwnerSections } from '../filters';
import { InsightsTracksTable } from './insights-tracks-table';
import { PulseInsightsSkeleton } from './insights-skeleton';
import {
  expandOwnerScopeRefs,
  getDescendantOwnerRefs,
  parseOwnerParamsFromUrl,
  SELECTED_OWNERS_STORAGE_KEY,
  sortRefs,
  syncOwnerParamsToUrl,
  toCanonicalOwnerRef,
} from './insights-group-scope';
import { InsightsSummaryCard } from './insights-summary-card';
import { getComplianceColorVar } from '@internal/backstage-plugin-pulse-common';
import { insightsRouteRef } from '../../routes';

const toStringArray = (keys: Key | Key[] | null): string[] => {
  if (Array.isArray(keys)) {
    return keys.map(String);
  }

  if (keys) {
    return [String(keys)];
  }

  return [];
};

type OwnerTagWithTooltipProps = {
  ownerRef: string;
  label: string;
  subgroupLabels: string[];
  selectedLabels?: ReadonlySet<string>;
};

type PulseInsightsPageProps = {
  initialOwnerRefs?: string[];
  embedded?: boolean;
};

const getInitialOwnerState = (
  initialOwnerRefs: string[],
  embedded: boolean,
) => {
  if (!embedded) {
    return parseOwnerParamsFromUrl(initialOwnerRefs);
  }

  const selectedOwnerRefs = [
    ...new Set(initialOwnerRefs.map(toCanonicalOwnerRef)),
  ];
  return {
    selectedOwnerRefs: sortRefs(selectedOwnerRefs),
    hasExplicitSelection: selectedOwnerRefs.length > 0,
  };
};

const OwnerTagWithTooltip = ({
  ownerRef,
  label,
  subgroupLabels,
  selectedLabels,
}: OwnerTagWithTooltipProps) => {
  if (subgroupLabels.length === 0) {
    return <Tag id={ownerRef}>{label}</Tag>;
  }

  const tooltipContent = [
    `Subgroups (${subgroupLabels.length}):`,
    ...subgroupLabels.map((sg, idx) => {
      const isSelected = selectedLabels?.has(sg) ?? false;
      const marker = isSelected ? '✓ ' : '';
      const selectedTag = isSelected ? ' (selected)' : '';
      const comma = idx === subgroupLabels.length - 1 ? '' : ',';
      return `${marker}${sg}${selectedTag}${comma}`;
    }),
  ].join('\n');

  return (
    <TooltipTrigger delay={300} closeDelay={300}>
      <Tag id={ownerRef}>
        {label} <RiInformationLine size={12} />
      </Tag>
      <Tooltip>{tooltipContent}</Tooltip>
    </TooltipTrigger>
  );
};

export const PulseInsightsPage = ({
  initialOwnerRefs = [],
  embedded = false,
}: PulseInsightsPageProps) => {
  const getInsightsPath = useRouteRef(insightsRouteRef);
  const [{ selectedOwnerRefs }, setUrlState] = useState(() => {
    const parsed = getInitialOwnerState(initialOwnerRefs, embedded);
    return { selectedOwnerRefs: parsed.selectedOwnerRefs };
  });
  const hasExplicitSelectionRef = useRef(
    getInitialOwnerState(initialOwnerRefs, embedded).hasExplicitSelection,
  );

  const {
    teamOptions,
    groupOptions,
    ownerByRef,
    labelByRef,
    primaryOwnedGroupRef,
  } = useTeamOwnerFilter();

  // Auto-populate with user's primary owned owner on first visit
  useEffect(() => {
    // Only auto-populate if no explicit selection was made and we haven't already tried
    if (hasExplicitSelectionRef.current || selectedOwnerRefs.length > 0) {
      return;
    }

    // Wait for groups to load before trying to resolve
    if (ownerByRef.size === 0) {
      return;
    }

    if (primaryOwnedGroupRef) {
      setUrlState(prev => ({
        ...prev,
        selectedOwnerRefs: [primaryOwnedGroupRef],
      }));
    }

    // Mark that we've attempted auto-population so we don't keep trying
    hasExplicitSelectionRef.current = true;
  }, [ownerByRef, primaryOwnedGroupRef, selectedOwnerRefs.length]);

  // Same sectioned "My team" / "All" owner options as the check/track detail pages.
  const ownerOptions = useMemo(
    () =>
      buildEntityOwnerSections(teamOptions, groupOptions, selectedOwnerRefs),
    [teamOptions, groupOptions, selectedOwnerRefs],
  );

  const selectableGroupRefs = useMemo(() => {
    if (groupOptions.length === 0) {
      return [];
    }

    const optionValues = new Set(groupOptions.map(option => option.value));
    return selectedOwnerRefs.filter(ownerRef => optionValues.has(ownerRef));
  }, [groupOptions, selectedOwnerRefs]);

  const ownerSubgroupsByRef = useMemo(() => {
    const map = new Map<string, string[]>();

    for (const ownerRef of selectedOwnerRefs) {
      const descendants = getDescendantOwnerRefs(ownerRef, ownerByRef);
      const descendantLabels = descendants
        .map(descRef => labelByRef.get(descRef))
        .filter((l): l is string => l !== undefined)
        .sort((a, b) => a.localeCompare(b));

      map.set(ownerRef, descendantLabels);
    }
    return map;
  }, [ownerByRef, labelByRef, selectedOwnerRefs]);

  const selectedOwnerLabels = useMemo(
    () =>
      selectedOwnerRefs
        .map(groupRef => labelByRef.get(groupRef))
        .filter((label): label is string => Boolean(label)),
    [labelByRef, selectedOwnerRefs],
  );

  const selectedLabels = useMemo(
    () => new Set(selectedOwnerLabels),
    [selectedOwnerLabels],
  );

  useEffect(() => {
    const onPopState = () => {
      setUrlState(parseOwnerParamsFromUrl(initialOwnerRefs));
    };

    if (!embedded) {
      globalThis.window?.addEventListener('popstate', onPopState);
    }

    return () => {
      if (!embedded) {
        globalThis.window?.removeEventListener('popstate', onPopState);
      }
    };
  }, [embedded, initialOwnerRefs]);

  const ownerScopeRefs = useMemo(
    () => expandOwnerScopeRefs(selectedOwnerRefs, ownerByRef),
    [ownerByRef, selectedOwnerRefs],
  );

  useEffect(() => {
    if (embedded) {
      return;
    }

    syncOwnerParamsToUrl(selectedOwnerRefs);
  }, [embedded, selectedOwnerRefs]);

  useEffect(() => {
    if (embedded || globalThis.window === undefined) {
      return;
    }

    globalThis.localStorage.setItem(
      SELECTED_OWNERS_STORAGE_KEY,
      JSON.stringify(selectedOwnerRefs),
    );
  }, [embedded, selectedOwnerRefs]);

  const formatCompliance = (compliance: number) =>
    compliance === 100 ? '100%' : `${compliance.toFixed(1)}%`;

  const { value, loading, error } = useInsightsTracks(ownerScopeRefs);

  const handleOwnerSelectionChange = useCallback((keys: Key | Key[] | null) => {
    const nextSelectedOwnerRefs = toStringArray(keys).map(element =>
      toCanonicalOwnerRef(element),
    );
    setUrlState(prev => ({
      ...prev,
      selectedOwnerRefs: sortRefs(nextSelectedOwnerRefs),
    }));
  }, []);

  const headerOwnerPicker = useMemo(
    () => (
      <Select
        icon={<RiTeamFill />}
        aria-label="Select owners to analyze"
        placeholder="Select owners..."
        selectionMode="multiple"
        searchable
        options={ownerOptions}
        value={selectableGroupRefs}
        onChange={handleOwnerSelectionChange}
        isDisabled={embedded || ownerOptions.length === 0}
        style={{ width: '100%' }}
      />
    ),
    [embedded, ownerOptions, handleOwnerSelectionChange, selectableGroupRefs],
  );

  const insightsContent = (
    <>
      <Box pb="2">
        <Flex align="center" gap="2">
          <Box style={{ flex: 1 }}>{headerOwnerPicker}</Box>
          {embedded && getInsightsPath ? (
            <TooltipTrigger delay={100} closeDelay={0}>
              <ButtonLink
                variant="tertiary"
                href={getInsightsPath()}
                iconStart={<RiArrowRightLine />}
                aria-label="View all Pulse insights"
              />
              <Tooltip>Go to Insights</Tooltip>
            </TooltipTrigger>
          ) : null}
        </Flex>
      </Box>
      {loading ? (
        <PulseInsightsSkeleton />
      ) : (
        <Flex direction="column" gap="6">
          <Grid.Root columns={{ initial: '1', sm: '2', md: '4' }} gap="4">
            <Grid.Item>
              <Card style={{ height: '100%' }}>
                <CardBody>
                  <Flex direction="column" gap="2">
                    <Text variant="body-large" color="secondary">
                      Owners In Scope
                    </Text>
                    <Text
                      variant="body-large"
                      weight="bold"
                      style={{ fontSize: '24px' }}
                    >
                      {value?.summary.groupsInScope ?? ownerScopeRefs.length}
                    </Text>
                    <Text variant="body-small" color="secondary">
                      {selectedOwnerRefs.length} selected
                      {ownerScopeRefs.length > selectedOwnerRefs.length
                        ? ` + ${
                            ownerScopeRefs.length - selectedOwnerRefs.length
                          } subgroups`
                        : ''}
                    </Text>
                    {selectedOwnerRefs.length > 0 && (
                      <Flex direction="column" gap="2">
                        <TagGroup
                          aria-label="Selected owners"
                          onRemove={
                            embedded
                              ? undefined
                              : keys => {
                                  const removedRefs = new Set(
                                    [...keys].map(String),
                                  );
                                  const nextRefs = selectedOwnerRefs.filter(
                                    ref => !removedRefs.has(ref),
                                  );
                                  setUrlState(prev => ({
                                    ...prev,
                                    selectedOwnerRefs: sortRefs(nextRefs),
                                  }));
                                }
                          }
                        >
                          {selectedOwnerRefs.map(ownerRef => {
                            const label = labelByRef.get(ownerRef) ?? ownerRef;
                            return (
                              <OwnerTagWithTooltip
                                key={ownerRef}
                                ownerRef={ownerRef}
                                label={label}
                                subgroupLabels={
                                  ownerSubgroupsByRef.get(ownerRef) ?? []
                                }
                                selectedLabels={selectedLabels}
                              />
                            );
                          })}
                        </TagGroup>
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() =>
                            setUrlState(prev => ({
                              ...prev,
                              selectedOwnerRefs: [],
                            }))
                          }
                          isDisabled={embedded}
                          style={{ alignSelf: 'flex-start' }}
                        >
                          Clear all
                        </Button>
                      </Flex>
                    )}
                  </Flex>
                </CardBody>
              </Card>
            </Grid.Item>
            <Grid.Item>
              <InsightsSummaryCard
                title="Entities In Scope"
                value={value?.summary.entitiesInScope ?? 0}
                subtitle={`Across ${ownerScopeRefs.length} group(s)`}
              />
            </Grid.Item>
            <Grid.Item>
              <InsightsSummaryCard
                title="Tracks In Scope"
                value={value?.summary.tracksInScope ?? 0}
              />
            </Grid.Item>
            <Grid.Item>
              <Card style={{ height: '100%' }}>
                <CardBody>
                  <Flex direction="column" gap="3">
                    <Text variant="body-large" color="secondary">
                      Average Compliance
                    </Text>
                    <Flex align="center" gap="3">
                      <Text
                        variant="body-large"
                        weight="bold"
                        style={{ fontSize: '32px', minWidth: '60px' }}
                      >
                        {value
                          ? formatCompliance(value.summary.averageCompliance)
                          : '--'}
                      </Text>
                      <div
                        style={{
                          flex: 1,
                          height: '12px',
                          background: 'var(--bui-bg-neutral-2)',
                          borderRadius: 'var(--bui-radius-3)',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            height: '100%',
                            width: `${value?.summary.averageCompliance ?? 0}%`,
                            background: value
                              ? getComplianceColorVar(
                                  value.summary.averageCompliance,
                                )
                              : 'transparent',
                            transition: 'width 0.3s ease',
                          }}
                        />
                      </div>
                    </Flex>
                  </Flex>
                </CardBody>
              </Card>
            </Grid.Item>
          </Grid.Root>

          {error && (
            <Text color="danger">Failed to load tracks: {error.message}</Text>
          )}

          <InsightsTracksTable
            rows={value?.rows ?? []}
            entityOwnerRefs={selectedOwnerRefs}
          />
        </Flex>
      )}
    </>
  );

  if (embedded) {
    return insightsContent;
  }

  return (
    <>
      <Header
        title="Insights"
        description="Select one or more owner groups to define scope. Insights automatically includes descendant subgroups and aggregates entities, tracks, and average compliance across the full scope."
      />
      <Container>{insightsContent}</Container>
    </>
  );
};
